import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import './ProfilePage.css';

const ICON = {
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  bolt: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  hash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  logout: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  moon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  sun: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  dot: <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="4"/></svg>,
};

const ROLE_DESCRIPTIONS = {
  admin: 'Full system access. Can manage users, content, and settings.',
  manager: 'Can manage all content and view administrative information.',
  editor: 'Can create, edit, and delete content across the platform.',
  viewer: 'Read-only access to dashboard content and reports.',
};

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function timeSince(value) {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
  if (days < 1) return 'today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  if (days < 365) return `${Math.floor(days / 30)} mo`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return months ? `${years}y ${months}mo` : `${years}y`;
}

export default function ProfilePage() {
  const { user, isAdmin, isEditor, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(null);

  const initials = useMemo(() => {
    const src = user?.full_name || user?.username || 'U';
    const parts = src.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  }, [user]);

  if (!user) return null;

  const handleCopy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1400);
    } catch {
      /* ignore */
    }
  };

  const handleLogout = () => {
    if (window.confirm('Sign out of your account?')) {
      logout();
      navigate('/login');
    }
  };

  const memberSince = formatDate(user.created_at);
  const memberFor = timeSince(user.created_at);
  const accessLevel = isAdmin ? 'Full Access' : isEditor ? 'Editor Access' : 'View Access';
  const roleDesc = ROLE_DESCRIPTIONS[user.role] || 'Standard platform user.';

  const permissions = [
    { label: 'View Content', granted: true, group: 'Content' },
    { label: 'Edit Content', granted: isEditor, group: 'Content' },
    { label: 'Create Content', granted: isEditor, group: 'Content' },
    { label: 'Delete Content', granted: isEditor, group: 'Content' },
    { label: 'Manage Users', granted: isAdmin, group: 'Admin' },
    { label: 'View Messages', granted: isAdmin, group: 'Admin' },
    { label: 'Manage Newsletter', granted: isAdmin, group: 'Admin' },
    { label: 'System Settings', granted: isAdmin, group: 'Admin' },
  ];
  const grantedCount = permissions.filter((p) => p.granted).length;
  const permPct = Math.round((grantedCount / permissions.length) * 100);

  return (
    <div className="profile-page">
      {/* HERO */}
      <div className="profile-hero">
        <div className="profile-hero__bg" aria-hidden />
        <div className="profile-hero__main">
          <div className="profile-hero__avatar">
            <span>{initials}</span>
            <span
              className={`profile-hero__status ${user.is_active ? 'is-active' : 'is-inactive'}`}
              title={user.is_active ? 'Active' : 'Inactive'}
            />
          </div>
          <div className="profile-hero__info">
            <div className="profile-hero__chips">
              <span className={`profile-chip profile-chip--${user.role}`}>
                {ICON.shield}
                <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
              </span>
              <span className={`profile-chip ${user.is_active ? 'profile-chip--ok' : 'profile-chip--off'}`}>
                {ICON.dot}
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
              {memberFor && (
                <span className="profile-chip profile-chip--muted">
                  {ICON.calendar}
                  Member {memberFor}
                </span>
              )}
            </div>
            <h1 className="profile-hero__name">{user.full_name || user.username}</h1>
            <p className="profile-hero__email">
              {ICON.mail}
              <span>{user.email}</span>
            </p>
            <p className="profile-hero__bio">{roleDesc}</p>
          </div>
        </div>

        <div className="profile-hero__actions">
          <button className="profile-action-btn" onClick={toggleTheme} aria-label="Toggle theme">
            <span className="profile-action-btn__icon">{isDark ? ICON.sun : ICON.moon}</span>
            <span className="profile-action-btn__label">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <button className="profile-action-btn" onClick={() => navigate('/dashboard')} aria-label="Go to dashboard">
            <span className="profile-action-btn__icon">{ICON.bolt}</span>
            <span className="profile-action-btn__label">Dashboard</span>
          </button>
          <button className="profile-action-btn profile-action-btn--danger" onClick={handleLogout} aria-label="Sign out">
            <span className="profile-action-btn__icon">{ICON.logout}</span>
            <span className="profile-action-btn__label">Sign Out</span>
          </button>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="profile-stats-row">
        <StatTile icon={ICON.shield} label="Role" value={user.role} accent />
        <StatTile icon={ICON.bolt} label="Access" value={accessLevel} />
        <StatTile icon={ICON.check} label="Status" value={user.is_active ? 'Active' : 'Inactive'} positive={user.is_active} />
        <StatTile icon={ICON.calendar} label="Member Since" value={memberSince || '—'} />
        <StatTile icon={ICON.hash} label="User ID" value={`#${user.id}`} mono />
        <StatTile icon={ICON.shield} label="Permissions" value={`${grantedCount}/${permissions.length}`} sub={`${permPct}%`} />
      </div>

      {/* GRID */}
      <div className="profile-grid">
        {/* Account Information */}
        <section className="profile-card profile-card--span-2">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Account Information</h2>
              <p className="profile-card__sub">Your identity on the platform</p>
            </div>
            <span className="profile-card__badge">Read-only</span>
          </header>
          <div className="profile-info-grid">
            <InfoCell icon={ICON.user} label="Full Name" value={user.full_name || '—'} />
            <InfoCell icon={ICON.user} label="Username" value={user.username} copyable copied={copied === 'username'} onCopy={() => handleCopy('username', user.username)} />
            <InfoCell icon={ICON.mail} label="Email" value={user.email} copyable copied={copied === 'email'} onCopy={() => handleCopy('email', user.email)} />
            <InfoCell icon={ICON.shield} label="Role" value={user.role} capitalize />
            <InfoCell icon={ICON.hash} label="User ID" value={String(user.id)} mono copyable copied={copied === 'id'} onCopy={() => handleCopy('id', user.id)} />
            <InfoCell icon={ICON.calendar} label="Member Since" value={memberSince || 'Unknown'} sub={memberFor ? `${memberFor} ago` : undefined} />
          </div>
        </section>

        {/* Preferences */}
        <section className="profile-card">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Preferences</h2>
              <p className="profile-card__sub">Customize your experience</p>
            </div>
          </header>
          <div className="profile-pref-list">
            <PrefRow
              icon={isDark ? ICON.moon : ICON.sun}
              title="Dark Mode"
              desc={isDark ? 'Currently using dark theme' : 'Currently using light theme'}
              active={isDark}
              onToggle={toggleTheme}
            />
            <PrefRow
              icon={ICON.lock}
              title="Two-Factor Auth"
              desc="Coming soon — extra layer of security"
              disabled
            />
            <PrefRow
              icon={ICON.mail}
              title="Email Notifications"
              desc="Coming soon — manage notification preferences"
              disabled
            />
          </div>
        </section>

        {/* Access Summary */}
        <section className="profile-card">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Access Summary</h2>
              <p className="profile-card__sub">Your role and capabilities</p>
            </div>
          </header>
          <div className="profile-access">
            <div className="profile-access__role">
              <span className={`profile-access__badge profile-access__badge--${user.role}`}>{user.role}</span>
              <p className="profile-access__desc">{roleDesc}</p>
            </div>
            <div className="profile-access__bar">
              <div className="profile-access__bar-track">
                <div className="profile-access__bar-fill" style={{ width: `${permPct}%` }} />
              </div>
              <div className="profile-access__bar-meta">
                <span>{grantedCount} of {permissions.length} permissions</span>
                <span>{permPct}%</span>
              </div>
            </div>
          </div>
        </section>

        {/* Permissions */}
        <section className="profile-card profile-card--span-2">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Permissions Matrix</h2>
              <p className="profile-card__sub">What you can do on this platform</p>
            </div>
            <span className="profile-card__badge">{grantedCount}/{permissions.length}</span>
          </header>
          <div className="profile-perms-grid">
            {permissions.map((p) => (
              <div key={p.label} className={`profile-perm ${p.granted ? 'is-granted' : 'is-denied'}`}>
                <span className="profile-perm__icon">
                  {p.granted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  )}
                </span>
                <div className="profile-perm__body">
                  <span className="profile-perm__label">{p.label}</span>
                  <span className="profile-perm__group">{p.group}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Security */}
        <section className="profile-card">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Security</h2>
              <p className="profile-card__sub">Keep your account safe</p>
            </div>
          </header>
          <ul className="profile-security">
            <li className="profile-security__item">
              <span className="profile-security__dot is-ok" />
              <div>
                <strong>Password</strong>
                <span>Strong authentication enabled</span>
              </div>
            </li>
            <li className="profile-security__item">
              <span className="profile-security__dot is-ok" />
              <div>
                <strong>Active Session</strong>
                <span>Currently signed in on this device</span>
              </div>
            </li>
            <li className="profile-security__item">
              <span className="profile-security__dot is-warn" />
              <div>
                <strong>Two-Factor Auth</strong>
                <span>Not yet available</span>
              </div>
            </li>
          </ul>
        </section>

        {/* Tips */}
        <section className="profile-card profile-card--accent">
          <header className="profile-card__header">
            <div>
              <h2 className="profile-card__title">Tips & Shortcuts</h2>
              <p className="profile-card__sub">Get the most out of the dashboard</p>
            </div>
          </header>
          <ul className="profile-tips">
            <li><span className="profile-tips__kbd">⌘ K</span> Quick search across content</li>
            <li><span className="profile-tips__kbd">G + D</span> Jump to Dashboard</li>
            <li><span className="profile-tips__kbd">⇧ ?</span> Show keyboard shortcuts</li>
            <li>Inline edit any field with a click</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, sub, accent, positive, mono }) {
  return (
    <div className={`profile-stat ${accent ? 'profile-stat--accent' : ''} ${positive ? 'profile-stat--ok' : ''}`}>
      <div className="profile-stat__icon">{icon}</div>
      <div className="profile-stat__body">
        <span className="profile-stat__label">{label}</span>
        <span className={`profile-stat__value ${mono ? 'is-mono' : ''}`} style={{ textTransform: label === 'Role' ? 'capitalize' : undefined }}>
          {value}
        </span>
        {sub && <span className="profile-stat__sub">{sub}</span>}
      </div>
    </div>
  );
}

function InfoCell({ icon, label, value, sub, mono, capitalize, copyable, onCopy, copied }) {
  return (
    <div className="profile-info-cell">
      <div className="profile-info-cell__icon">{icon}</div>
      <div className="profile-info-cell__body">
        <span className="profile-info-cell__label">{label}</span>
        <span
          className={`profile-info-cell__value ${mono ? 'is-mono' : ''}`}
          style={{ textTransform: capitalize ? 'capitalize' : undefined }}
        >
          {value}
        </span>
        {sub && <span className="profile-info-cell__sub">{sub}</span>}
      </div>
      {copyable && (
        <button
          className={`profile-info-cell__copy ${copied ? 'is-copied' : ''}`}
          onClick={onCopy}
          aria-label={`Copy ${label}`}
          title={copied ? 'Copied!' : 'Copy'}
        >
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          )}
        </button>
      )}
    </div>
  );
}

function PrefRow({ icon, title, desc, active, onToggle, disabled }) {
  return (
    <div className={`profile-pref ${disabled ? 'is-disabled' : ''}`}>
      <div className="profile-pref__icon">{icon}</div>
      <div className="profile-pref__body">
        <span className="profile-pref__title">{title}</span>
        <span className="profile-pref__desc">{desc}</span>
      </div>
      <button
        className={`profile-toggle ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        aria-label={`Toggle ${title}`}
      >
        <span className="profile-toggle__thumb" />
      </button>
    </div>
  );
}
